/**
 * Tiny "vX.Y · sha · timestamp" pill anchored bottom-right of every page.
 *
 * Why: during a deploy rollout, we need to tell AT A GLANCE whether a user
 * is running the freshly-deployed bundle or an old cached one. We pair this
 * with the service-worker cache bump so any "I'm seeing old UI" report is
 * resolvable in 5 seconds by asking "what does the pill show?".
 *
 * Source of truth: /version.json (written at build time by
 * frontend/scripts/inject-app-version.js). Falls back silently if missing.
 */
import React, { useEffect, useState } from 'react';
import { FiGitCommit } from 'react-icons/fi';

export default function AppVersionBadge() {
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);
  const [serverInfo, setServerInfo] = useState(null);

  useEffect(() => {
    // Cache-bust with the current epoch so SW can't serve an old copy.
    fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' })
      .then(r => (r.ok ? r.json() : null))
      .then(j => j && setInfo(j))
      .catch(() => {});
  }, []);

  // Only when the merchant / admin opens the details — keep the ping cheap.
  useEffect(() => {
    if (!open || serverInfo) return;
    const base = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${base}/api/health/version`, { cache: 'no-store' })
      .then(r => (r.ok ? r.json() : null))
      .then(j => j && setServerInfo(j))
      .catch(() => {});
  }, [open, serverInfo]);

  if (!info) return null;

  const { version, git_sha: sha, built_at: built } = info;
  const shortBuilt = built ? new Date(built).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : '';

  const mismatch =
    serverInfo &&
    info.git_sha && serverInfo.git_sha &&
    info.git_sha !== serverInfo.git_sha;

  return (
    <div
      className="fixed bottom-3 right-3 z-40 select-none"
      data-testid="app-version-badge"
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1.5 px-2 py-1 text-[10px] font-mono rounded-full border shadow-sm backdrop-blur-sm transition-all ${
          mismatch
            ? 'bg-amber-100/90 text-amber-900 border-amber-300'
            : 'bg-white/85 text-gray-600 border-gray-200 hover:bg-white hover:text-gray-800'
        }`}
        title="Click for deploy details"
        aria-label={`App version ${version}, build ${sha}`}
      >
        <FiGitCommit size={10} />
        <span>v{version}</span>
        <span className="opacity-50">·</span>
        <span>{sha}</span>
        {shortBuilt && <>
          <span className="opacity-50">·</span>
          <span>{shortBuilt}</span>
        </>}
      </button>

      {open && (
        <div
          className="absolute bottom-8 right-0 min-w-[240px] rounded-lg border border-gray-200 bg-white shadow-xl p-3 text-xs text-gray-700 space-y-1.5"
          data-testid="app-version-details"
        >
          <div className="font-semibold text-gray-900 flex items-center gap-1">
            <FiGitCommit size={12} /> Deploy Info
          </div>
          <div className="border-t border-gray-100 pt-1.5 space-y-1 font-mono">
            <div>frontend · <span className="text-gray-900">{sha}</span></div>
            <div className="opacity-70">{info.git_branch} · {built}</div>
            {serverInfo ? (
              <>
                <div className="pt-1 border-t border-gray-100">
                  backend  · <span className="text-gray-900">{serverInfo.git_sha || 'unknown'}</span>
                </div>
                <div className="opacity-70">
                  {serverInfo.git_branch || ''}
                  {serverInfo.built_at ? ` · ${serverInfo.built_at}` : ''}
                </div>
              </>
            ) : (
              <div className="opacity-50">backend · loading…</div>
            )}
          </div>
          {mismatch && (
            <div className="mt-1.5 pt-1.5 border-t border-amber-200 text-[11px] text-amber-800 bg-amber-50 -mx-3 -mb-3 p-2 rounded-b-lg">
              ⚠ Frontend / backend SHA mismatch. A deploy is mid-rollout or your browser cache is stale — try a hard refresh.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
